import { useCallback, useMemo, useState } from 'react';
import type { Milestone } from '@altyor-kronos/shared-types';
import { usePolledResource } from '../../core/data/usePolledResource';
import { apiList } from '../../core/data/restClient';

export interface MilestonesState {
  milestones: Array<{ id: string; data: Milestone }>;
  loading: boolean;
  error: Error | null;
  /**
   * Relance une lecture immédiate. Indispensable depuis la bascule REST : sans
   * `onSnapshot`, une écriture n'est visible qu'au prochain tick de poll (20 s),
   * et l'utilisateur voit son propre changement mettre 20 s à apparaître.
   * À appeler après chaque mutation de jalon.
   */
  refresh: () => void;
}

/**
 * Read a project's milestones subcollection, ordered by `order`.
 * Returns an empty idle state when projectId is falsy.
 */
export function useMilestones(projectId: string): MilestonesState {
  // La clé de `usePolledResource` pilote son effet : la bouger relance un fetch.
  const [version, setVersion] = useState(0);
  const { rows, loading, error } = usePolledResource<Milestone & { id: string }>(
    projectId ? `${projectId}:${version}` : null,
    () => apiList<Milestone & { id: string }>(`/projects/${projectId}/milestones`),
  );
  // Server milestones list has no orderBy — sort by `order` asc.
  const milestones = useMemo(
    () => rows.slice().sort((a, b) => a.order - b.order).map((r) => ({ id: r.id, data: r })),
    [rows],
  );
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  return { milestones, loading, error, refresh };
}
